import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';

import { EmptyState, SectionHeading, Surface, Button, pageStyle, eyebrowStyle } from '@kilat/ui';
import {
  OPERATOR_TRACK,
  isLessonUnlocked,
  trackProgress,
  type AcademyLesson,
  type AcademyProgressSignals,
} from '@kilat/shared';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { env } from '../lib/env.js';
import { academyRepoFor } from '../lib/repositories/academy-repo.js';
import { lifetimeStatsRepoFor } from '../lib/repositories/lifetime-stats-repo.js';

/**
 * The Operator Academy index (gap G19).
 *
 * The operator did not need more documentation — fourteen articles already
 * existed in four languages. What was missing was a path through them, and any
 * link at all from inside the product. Documentation people must leave the
 * product to find is documentation they do not read.
 *
 * Three decisions worth stating, because each of them is a way this could have
 * gone wrong:
 *
 *   - **It teaches, it never gates.** Nothing here blocks a feature, and a
 *     locked lesson is not withheld from a curious operator — it simply is not
 *     pushed at them until the screen it describes has something on it. The
 *     subtitle says so out loud, because a learning path that looks compulsory
 *     is one more thing an operator feels behind on.
 *   - **No article text lives here.** Every lesson is a link out to the existing
 *     help article. A copy inside the dashboard would be a second thing to keep
 *     in sync across four languages, and it would be the one that rots.
 *   - **Completing a section says so.** "You can take a booking and get paid.
 *     That's the hard part done." Finishing something and being TOLD you
 *     finished it are different experiences, and the second is the one that
 *     makes someone feel competent rather than perpetually behind.
 */
export function AcademyPage() {
  const { t, locale } = useI18n();
  const { user, claims } = useAuth();
  const uid = user?.uid ?? null;
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const [completed, setCompleted] = useState<ReadonlySet<string>>(new Set());
  const [signals, setSignals] = useState<AcademyProgressSignals>({
    hasOutlet: false,
    washesRecorded: 0,
    paidTickets: 0,
    customersKnown: 0,
    loyaltyConfigured: false,
  });
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    void (async () => {
      try {
        const [progress, stats] = await Promise.all([
          academyRepoFor(uid).get('operator'),
          tenantId ? lifetimeStatsRepoFor(tenantId).get('lifetime') : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setCompleted(new Set(progress?.completedLessonIds ?? []));
        setSignals({
          hasOutlet: true,
          washesRecorded: stats?.washCount ?? 0,
          // A recorded wash IS a paid ticket in this product — the counter is
          // incremented from ticketsHistory, which only receives collected
          // tickets. Kept as a separate signal so a future distinction does not
          // require rewriting the curriculum.
          paidTickets: stats?.washCount ?? 0,
          customersKnown: stats?.customerCount ?? 0,
          loyaltyConfigured: false,
        });
      } catch (e: unknown) {
        logger.warn('academy progress unavailable', { error: String(e) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, tenantId]);

  const markRead = useCallback(
    async (lessonId: string) => {
      if (!uid) return;
      const next = new Set(completed);
      next.add(lessonId);
      // Optimistic: reading a guide should not wait on a round trip, and the
      // worst case of being wrong is a tick that reverts.
      setCompleted(next);
      setSaveError(null);
      try {
        await academyRepoFor(uid).set('operator', {
          completedLessonIds: [...next],
        });
      } catch (e: unknown) {
        logger.error('academy progress save failed', { error: String(e), lessonId });
        setCompleted(completed);
        setSaveError(t('academy.saveFailed'));
      }
    },
    [uid, completed, t],
  );

  const progress = useMemo(() => trackProgress(completed, signals), [completed, signals]);

  const helpUrl = (slug: string) => `${env.marketingOrigin}/${locale}/help/${slug}`;

  if (loading) {
    return (
      <main style={pageStyle} aria-busy>
        <p style={eyebrowStyle}>{t('academy.eyebrow')}</p>
        <h1>{t('academy.title')}</h1>
        <p>{t('academy.loading')}</p>
      </main>
    );
  }

  return (
    <main style={pageStyle}>
      <p style={eyebrowStyle}>{t('academy.eyebrow')}</p>
      <h1>{t('academy.title')}</h1>
      <p style={subtitleStyle}>{t('academy.subtitle')}</p>

      {saveError ? (
        <p role="alert" style={errorStyle}>
          {saveError}
        </p>
      ) : null}

      {OPERATOR_TRACK.sections.map((section) => {
        const sp = progress.find((p) => p.sectionId === section.id);
        return (
          <Surface key={section.id} style={{ display: 'grid', gap: 'var(--space-3)' }}>
            {/* `trailing` carries the count so the heading stays one line and
                the progress reads as a status rather than a second title. */}
            <SectionHeading
              trailing={
                <span style={readStyle}>
                  {t('academy.sectionProgress', {
                    done: sp?.done ?? 0,
                    total: sp?.total ?? section.lessons.length,
                  })}
                </span>
              }
            >
              {t(section.titleKey)}
            </SectionHeading>

            {/* The competence line, and the reason a section exists at all. */}
            {sp?.complete ? <p style={masteryStyle}>{t(section.masteryKey)}</p> : null}

            <ul style={listStyle}>
              {section.lessons.map((lesson) => (
                <LessonRow
                  key={lesson.id}
                  lesson={lesson}
                  read={completed.has(lesson.id)}
                  unlocked={isLessonUnlocked(lesson, signals)}
                  href={helpUrl(lesson.helpSlug)}
                  onRead={() => void markRead(lesson.id)}
                />
              ))}
            </ul>
          </Surface>
        );
      })}

      {OPERATOR_TRACK.sections.length === 0 ? (
        <EmptyState title={t('academy.title')} body={t('academy.subtitle')} />
      ) : null}
    </main>
  );
}

function LessonRow({
  lesson,
  read,
  unlocked,
  href,
  onRead,
}: {
  readonly lesson: AcademyLesson;
  readonly read: boolean;
  readonly unlocked: boolean;
  readonly href: string;
  readonly onRead: () => void;
}) {
  const { t } = useI18n();
  return (
    <li style={rowStyle}>
      <span style={{ fontWeight: read ? 400 : 600, opacity: unlocked ? 1 : 0.55 }}>
        {t(lesson.titleKey)}
      </span>

      <span style={actionsStyle}>
        {/* A locked lesson is still openable. Teaching offers; it never gates —
            hiding the link from someone actively looking for it would be the
            opposite of help. */}
        <a href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {t('academy.openArticle')}
        </a>
        {read ? (
          <span style={readStyle}>{t('academy.read')}</span>
        ) : (
          <Button variant="quiet" onClick={onRead}>
            {t('academy.markRead')}
          </Button>
        )}
      </span>

      {!unlocked && !read ? <span style={hintStyle}>{t('academy.lockedHint')}</span> : null}
    </li>
  );
}

const subtitleStyle: CSSProperties = {
  margin: '0 0 var(--space-4)',
  color: 'var(--color-on-surface-muted)',
  maxWidth: '60ch',
};

const listStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: 'var(--space-2)',
};

const rowStyle: CSSProperties = {
  display: 'grid',
  gap: 'var(--space-1, 4px)',
  padding: 'var(--space-2) 0',
  borderBottom: '1px solid var(--color-border)',
};

const actionsStyle: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-3)',
  alignItems: 'center',
  flexWrap: 'wrap',
};

const linkStyle: CSSProperties = { color: 'var(--color-primary)', fontWeight: 600 };

const readStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const hintStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const masteryStyle: CSSProperties = {
  margin: 0,
  fontWeight: 600,
  color: 'var(--color-primary)',
};

const errorStyle: CSSProperties = { margin: 0, color: 'var(--color-warning, #b45309)' };
